import { Loader } from 'lucide-react';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { filesApi, filesApiQueryKeys } from '@/lib/api/filesApi.ts';
import { FileFieldCellDto } from '@/components/table/_fieldDefinitions/file/FileFieldCellDto.ts';
import { splitFileName } from '@/components/table/_fieldDefinitions/file/fileName.utils.ts';
import { getFileIcon } from '@/components/table/_fieldDefinitions/file/fileIcon.utils.ts';
import { toast } from 'sonner';

interface FileFieldCellProps {
  file: FileFieldCellDto;
}

const DOWNLOAD_URL_STALE_TIME = 1000 * 30;

export const FileFieldCell = ({ file }: FileFieldCellProps) => {
  const queryClient = useQueryClient();
  const [isResolving, setIsResolving] = useState(false);
  const { head, tail } = splitFileName(file.fileName);
  const FileIcon = getFileIcon(file.contentType, file.fileName);

  const prefetch = () => {
    queryClient.prefetchQuery({
      queryKey: filesApiQueryKeys.downloadUrl(file.fileId),
      queryFn: () => filesApi.getDownloadUrl(file.fileId),
      staleTime: DOWNLOAD_URL_STALE_TIME,
    });
  };

  const handleClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setIsResolving(true);
    try {
      const url = await queryClient.fetchQuery({
        queryKey: filesApiQueryKeys.downloadUrl(file.fileId),
        queryFn: () => filesApi.getDownloadUrl(file.fileId),
        staleTime: DOWNLOAD_URL_STALE_TIME,
      });
      window.open(url.downloadUrl, '_blank', 'noopener,noreferrer');
    } catch {
      toast.error('Nie udało się pobrać pliku');
    } finally {
      setIsResolving(false);
    }
  };

  return (
    <button
      type="button"
      onMouseEnter={prefetch}
      onFocus={prefetch}
      onClick={handleClick}
      title={`Pobierz ${file.fileName}`}
      aria-label={`Pobierz ${file.fileName}`}
      disabled={isResolving}
      className="group/file inline-flex max-w-full items-center gap-1.5 text-left disabled:cursor-not-allowed"
    >
      {isResolving ? (
        <Loader className="size-3.5 shrink-0 animate-spin text-neutral-500" />
      ) : (
        <FileIcon className="size-3.5 shrink-0 text-neutral-500" aria-hidden />
      )}
      {/* Ucięcie od środka robi CSS: głowa się skraca, ogon z rozszerzeniem nigdy. */}
      <span className="flex min-w-0 font-semibold text-base-text group-hover/file:underline">
        <span className="truncate">{head}</span>
        <span className="shrink-0">{tail}</span>
      </span>
    </button>
  );
};
